import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';

import ActivationReadinessCard from './sections/ActivationReadinessCard/ActivationReadinessCard';
import ActivationSetupCard from './sections/ActivationSetupCard/ActivationSetupCard';
import ClientDataCard, { type IClientDataSavedResult } from './sections/ClientDataCard/ClientDataCard';
import ProfileActions from './sections/ProfileActions/ProfileActions';
import QuotedCard from './sections/QuotesCard/QuotedCard';
import SecondaryHeader from '../../../../shared/components/Globals/Headers/SecondaryHeader/SecondaryHeader';
import LoadingFullScreen from '../../../../shared/components/Globals/LoadingFullScreen/LoadingFullScreen';
import ProspectActivationModal from '../../../../shared/components/Modals/ProspectActivationModal/ProspectActivationModal';
import { PROSPECTS_ROUTE, buildQuoteCreateRoute } from '../../../../shared/constants/auth';
import { PROSPECT_STATUS_BADGE } from '../../../../shared/constants/prospects';
import { CLICK_ACTION, CONFIRM_ACTIVATION, GET_QUOTE, SOURCE_PROFILE } from '../../../../shared/constants/tagManager';
import { useProspects } from '../../../../shared/hooks/useProspect/useProspect';
import { useProspectStatusUpdate } from '../../../../shared/hooks/useProspectStatusUpdate/useProspectStatusUpdate';
import { useUser } from '../../../../shared/hooks/useUser/useUser';
import type { IDataCheck } from '../../../../shared/interfaces/checks';
import type {
  IBillingDraftFields,
  IContactDraftFields,
  ICreateProspectContact,
  IProspect,
  IProspectContact,
  IUpdateProspectContact,
  IUpdateProspectInput
} from '../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../shared/interfaces/quotes';
import ProspectsService from '../../../../shared/services/api/prospects/prospects';
import QuotesService from '../../../../shared/services/api/quotes/quotes';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../shared/translations/prospects';
import { ProspectContactType, ProspectStatus } from '../../../../shared/types/prospects';
import { getActivationQuotes } from '../../../../shared/utils/checks/activationChecks';
import {
  getBillingContact,
  getPrimaryContact,
  getSalesModel,
  isAppOnly,
  requiresHeadcountMinimum
} from '../../../../shared/utils/prospects/prospects';
import { excludeInactiveQuotes, isActivationCensusFile } from '../../../../shared/utils/quotes/quotes';
import './ProspectProfile.scss';

const getProspectStatusBadge = (status: string) =>
  PROSPECT_STATUS_BADGE[status] ?? {
    variant: 'neutral',
    labelKey: TRANSLATIONS_PROSPECTS.status.unknown
  };

const ProspectProfile = () => {
  const { id } = useParams<{ id: string }>();
  const prospectId = Number(id);
  const { brokerId, loading: userLoading } = useUser();
  const { updateProspect, createContact, updateContact, refresh, setSelectedId } = useProspects();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [prospect, setProspect] = useState<IProspect | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const fetchProspect = useCallback(
    async (silent = false) => {
      // Invalid id in the URL → genuinely not found.
      if (Number.isNaN(prospectId)) {
        setProspect(null);
        setInitialLoading(false);
        return;
      }
      if (userLoading || !brokerId) return;
      try {
        if (!silent) setInitialLoading(true);
        const prospects = await ProspectsService.getProspects(brokerId, { prospectId });
        setProspect(prospects.find((item) => item.id === prospectId) ?? null);
      } catch {
        setProspect(null);
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [brokerId, userLoading, prospectId]
  );

  useEffect(() => {
    fetchProspect();
  }, [fetchProspect]);

  useEffect(() => {
    if (!Number.isNaN(prospectId)) {
      setSelectedId(prospectId);
    }
  }, [prospectId, setSelectedId]);

  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [rawQuotes, setRawQuotes] = useState<IQuote[]>([]);

  const [censusRemovedFor, setCensusRemovedFor] = useState<number[]>([]);

  const quotes = useMemo(
    () =>
      censusRemovedFor.length === 0
        ? rawQuotes
        : rawQuotes.map((quote) =>
            censusRemovedFor.includes(quote.id)
              ? { ...quote, files: quote.files.filter((file) => !isActivationCensusFile(file)) }
              : quote
          ),
    [rawQuotes, censusRemovedFor]
  );
  const [activeSetupQuoteId, setActiveSetupQuoteId] = useState<number | null>(null);
  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [companyDraft, setCompanyDraft] = useState<Partial<IProspect> | null>(null);
  const [contactDraft, setContactDraft] = useState<IContactDraftFields | null>(null);
  const [billingDraft, setBillingDraft] = useState<IBillingDraftFields | null>(null);

  const liveProspect = useMemo(() => {
    if (!prospect) return null;
    let live: IProspect = companyDraft ? { ...prospect, ...companyDraft } : prospect;

    if (contactDraft) {
      const primary = getPrimaryContact(prospect);
      const base: IProspectContact = primary ?? { id: 0, contactType: ProspectContactType.Administrative, name: '' };
      const updatedContact: IProspectContact = {
        ...base,
        name: contactDraft.name.trim(),
        title: contactDraft.title.trim() || undefined,
        email: contactDraft.email.trim() || undefined,
        phone: contactDraft.phone.trim() || undefined
      };
      live = {
        ...live,
        contacts: [...live.contacts.filter((c) => c.contactType !== ProspectContactType.Administrative), updatedContact]
      };
    }

    if (billingDraft) {
      const withoutBilling = live.contacts.filter((c) => c.contactType !== ProspectContactType.Billing);
      if (billingDraft.sameAsPrimary) {
        live = { ...live, contacts: withoutBilling };
      } else {
        const persistedBilling = getBillingContact(prospect);
        const base: IProspectContact = persistedBilling ?? {
          id: 0,
          contactType: ProspectContactType.Billing,
          name: ''
        };
        const updatedBilling: IProspectContact = {
          ...base,
          name: billingDraft.name.trim(),
          title: billingDraft.title.trim() || undefined,
          email: billingDraft.email.trim() || undefined,
          phone: billingDraft.phone.trim() || undefined
        };
        live = { ...live, contacts: [...withoutBilling, updatedBilling] };
      }
    }

    return live;
  }, [prospect, companyDraft, contactDraft, billingDraft]);

  const breadcrumbItems = [
    { label: t(TRANSLATIONS_PROSPECTS.profile.back), onClick: () => navigate(PROSPECTS_ROUTE) },
    { label: prospect?.companyName ?? '' }
  ];

  const handleNewQuote = useCallback(() => {
    TagManagerService.pushEvent(CLICK_ACTION, GET_QUOTE, SOURCE_PROFILE);
    navigate(buildQuoteCreateRoute(prospectId));
  }, [navigate, prospectId]);

  const handleStatusUpdateSuccess = useCallback(() => {
    refresh();
    fetchProspect(true);
  }, [refresh, fetchProspect]);

  const { handleMarkAsLost, handleMarkAsInactive, isUpdatingStatus } = useProspectStatusUpdate({
    brokerId: brokerId ?? undefined,
    prospectId,
    currentStatus: prospect?.status,
    onSuccess: handleStatusUpdateSuccess
  });

  const fetchQuotes = useCallback(
    async (silent = false) => {
      if (!brokerId || Number.isNaN(prospectId)) {
        setRawQuotes([]);
        if (!silent) setLoadingQuotes(false);
        return;
      }

      try {
        if (!silent) setLoadingQuotes(true);
        const data = await QuotesService.getQuotes(brokerId, prospectId);
        setRawQuotes(excludeInactiveQuotes(data));

        setCensusRemovedFor([]);
      } catch {
        setRawQuotes([]);
      } finally {
        if (!silent) setLoadingQuotes(false);
      }
    },
    [brokerId, prospectId]
  );

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleUpdateProspect = useCallback(
    async (input: IUpdateProspectInput) => {
      if (!prospect) return;
      await updateProspect(prospect.id, input);
    },
    [prospect, updateProspect]
  );

  const handleCreateContact = useCallback(
    async (input: ICreateProspectContact) => {
      if (!prospect) return;
      await createContact(prospect.id, input);
    },
    [prospect, createContact]
  );

  const handleUpdateContact = useCallback(
    async (contactId: number, input: IUpdateProspectContact) => {
      if (!prospect) return;
      await updateContact(prospect.id, contactId, input);
    },
    [prospect, updateContact]
  );

  const handleSaved = useCallback(
    async ({ salesModelChanged }: IClientDataSavedResult) => {
      await fetchProspect(true);
      if (salesModelChanged) await fetchQuotes(true);
    },
    [fetchProspect, fetchQuotes]
  );

  const focusActivationTarget = useCallback((quoteId?: number) => {
    const selector = quoteId ? '.activation-setup' : '.client-data-card';
    if (quoteId) setActiveSetupQuoteId(quoteId);
    requestAnimationFrame(() => {
      const card = document.querySelector(selector);
      if (!card) return;
      const header = card.querySelector<HTMLElement>('.expandable-card__header');
      if (header?.getAttribute('aria-expanded') === 'false') header.click();
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  useEffect(() => {
    if (initialLoading || loadingQuotes) return;
    const target = (location.state as { activationCheck?: { quoteId?: number } } | null)?.activationCheck;
    if (!target) return;
    focusActivationTarget(target.quoteId);
    navigate(location.pathname, { replace: true, state: null });
  }, [initialLoading, loadingQuotes, location, navigate, focusActivationTarget]);

  if (initialLoading) {
    return <LoadingFullScreen show />;
  }

  if (!prospect) {
    return (
      <div className="prospect-profile">
        <SecondaryHeader breadcrumbItems={breadcrumbItems} />
        <div className="prospect-profile__not-found">{t(TRANSLATIONS_PROSPECTS.profile.notFound)}</div>
      </div>
    );
  }

  const badge = getProspectStatusBadge(prospect.status);

  const acceptedQuotes = getActivationQuotes(quotes);
  const unselectedQuotes = quotes.filter((quote) => quote.status !== 'accepted');
  const isPendingActivation = prospect.status === ProspectStatus.PendingActivation;
  const containAcceptedQuotes = acceptedQuotes.length < 1;

  const titleQuoteCard = containAcceptedQuotes
    ? TRANSLATIONS_PROSPECTS.detail.quoted.sectionLabel
    : TRANSLATIONS_PROSPECTS.detail.quoted.unselectedSectionLabel;

  const handleQuoteStatusChange = () => {
    refresh();
    fetchQuotes(true);
  };

  const handleActivationCheckClick = (check: IDataCheck) => {
    focusActivationTarget(check.quoteId);
  };

  const handleQuoteUpdate = (updated: IQuote) => {
    setRawQuotes((prev) => prev.map((quote) => (quote.id === updated.id ? updated : quote)));
  };

  const handleCensusRemoved = (quoteId: number) => {
    setCensusRemovedFor((prev) => (prev.includes(quoteId) ? prev : [...prev, quoteId]));
  };

  const handleCensusChange = async () => {
    await fetchQuotes(true);
    refresh();
  };

  const handleOpenActivationModal = () => {
    setActivationModalOpen(true);
    TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_ACTIVATION, SOURCE_PROFILE);
  };

  const handleActivationRefresh = () => {
    refresh();
    fetchProspect();
    fetchQuotes(true);
  };

  const renderQuoteCards = () => {
    if (isPendingActivation) {
      return (
        <>
          {acceptedQuotes.length > 0 && (
            <QuotedCard
              prospect={prospect}
              quotes={acceptedQuotes}
              titleKey={TRANSLATIONS_PROSPECTS.detail.quoted.acceptedSectionLabel}
              showNewQuote={false}
              onQuoteStatusChange={handleQuoteStatusChange}
            />
          )}
          {unselectedQuotes.length > 0 && (
            <QuotedCard
              prospect={prospect}
              quotes={unselectedQuotes}
              titleKey={titleQuoteCard}
              showNewQuote={containAcceptedQuotes}
              onQuoteStatusChange={handleQuoteStatusChange}
            />
          )}
        </>
      );
    }
    return (
      <>
        {quotes.length > 0 && (
          <QuotedCard
            prospect={prospect}
            quotes={quotes}
            titleKey={TRANSLATIONS_PROSPECTS.detail.quoted.sectionLabel}
            showNewQuote
            onQuoteStatusChange={handleQuoteStatusChange}
          />
        )}
      </>
    );
  };

  return (
    <div className="prospect-profile">
      <SecondaryHeader breadcrumbItems={breadcrumbItems} badgeText={t(badge.labelKey)} badgeVariant={badge.variant} />

      <div className="prospect-profile__body">
        <div className="prospect-profile__main">
          <ClientDataCard
            prospect={prospect}
            onUpdate={handleUpdateProspect}
            onCreateContact={handleCreateContact}
            onUpdateContact={handleUpdateContact}
            onCompanyDraftChange={setCompanyDraft}
            onContactDraftChange={setContactDraft}
            onBillingDraftChange={setBillingDraft}
            onSaved={handleSaved}
            savedQuotesCount={quotes.length}
          />
          {isPendingActivation && (
            <ActivationSetupCard
              prospectId={prospectId}
              isAppOnly={isAppOnly(liveProspect ?? prospect)}
              requiresMinimum={requiresHeadcountMinimum(getSalesModel(liveProspect ?? prospect))}
              quotes={quotes}
              activeQuoteId={activeSetupQuoteId}
              appOnly={isAppOnly(prospect)}
              onActiveQuoteChange={setActiveSetupQuoteId}
              onQuoteUpdate={handleQuoteUpdate}
              onQuotesRefetch={handleCensusChange}
              onCensusRemoved={handleCensusRemoved}
            />
          )}
          {loadingQuotes ? (
            <div className="prospect-profile__quotes-skeleton" aria-hidden="true" />
          ) : (
            renderQuoteCards()
          )}
        </div>
        <aside className="prospect-profile__sidebar">
          <ActivationReadinessCard
            prospect={liveProspect ?? prospect}
            quotes={quotes}
            loading={loadingQuotes}
            onActivate={handleOpenActivationModal}
            onCheckClick={handleActivationCheckClick}
          />
          <ProfileActions
            prospect={prospect}
            quotes={quotes}
            onGetQuote={() => handleNewQuote()}
            onRefreshProspect={handleActivationRefresh}
            onMarkLost={handleMarkAsLost}
            onMarkInactive={handleMarkAsInactive}
            disabled={isUpdatingStatus}
          />
        </aside>
      </div>

      <ProspectActivationModal
        open={activationModalOpen}
        prospect={prospect}
        quoteIds={acceptedQuotes.map((quote) => quote.id)}
        quotes={acceptedQuotes}
        onClose={() => setActivationModalOpen(false)}
      />
    </div>
  );
};

export default ProspectProfile;
